import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ssoService from '../ssoService';
import { useAuthStore } from '../../../stores/authStore';

export const OIDCCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleOIDCCallback = async (): Promise<void> => {
      try {
        const code = searchParams.get('code');
        const accessToken = searchParams.get('access_token');
        const idToken = searchParams.get('id_token');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (errorParam) {
          throw new Error(errorDescription || `OAuth error: ${errorParam}`);
        }

        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashAccessToken = hashParams.get('access_token');
        const hashIdToken = hashParams.get('id_token');

        const finalAccessToken = hashAccessToken || accessToken || undefined;
        const finalIdToken = hashIdToken || idToken || undefined;

        if (!finalAccessToken && !finalIdToken && !code) {
          throw new Error('No authentication token or code found');
        }

        const response = await ssoService.handleSSOLogin(
          finalAccessToken,
          finalIdToken,
          code || undefined
        );

        login(response.user, response.access, response.refresh);

        const redirectTo = localStorage.getItem('sso_redirect_to') || '/';
        localStorage.removeItem('sso_redirect_to');
        navigate(redirectTo);
      } catch (err) {
        console.error('OIDC callback error:', err);
        const errorMessage = err instanceof Error ? err.message : 'SSO authentication failed';
        setError(errorMessage);
        setLoading(false);
      }
    };

    handleOIDCCallback();
  }, [searchParams, navigate, login]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[var(--color-primary-500)] border-r-transparent"></div>
          <p className="text-[var(--color-text-primary)]">Processing SSO authentication...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            SSO Authentication Error
          </h2>
          <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded-lg font-medium transition-colors"
            style={{
              backgroundColor: 'var(--color-primary-500)',
              color: 'white',
            }}
            type="button"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};


